// src/components/TherapistPreferences.js - Förenklad terapeut AI-inställningar
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TherapistPreferences = ({ token }) => {
  const [preferences, setPreferences] = useState({
    therapeutic_approaches: [],
    intervention_techniques: [],
    communication_style: 'warm_empathetic',
    response_style: 'detailed_explanatory',
    crisis_approach: 'balanced'
  });
  
  const [options, setOptions] = useState({
    therapeutic_approaches: [],
    intervention_techniques: [],
    communication_styles: [],
    response_styles: [],
    crisis_approaches: []
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Ladda alternativ först
      const optionsResponse = await axios.get(`${API_BASE}/api/therapist-preferences/options`);
      setOptions(optionsResponse.data);
      
      // Försök ladda befintliga inställningar (kan misslyckas om inga finns ännu)
      try {
        const prefsResponse = await axios.get(`${API_BASE}/api/therapist-preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setPreferences(prefsResponse.data);
      } catch (err) {
        // Inga inställningar finns ännu - använd standardvärden
        console.log('Inga befintliga inställningar hittades, använder standardvärden');
      }
      
    } catch (err) {
      setError('Misslyckades att ladda inställningsdata');
      console.error('Fel vid laddning av inställningar:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.put(`${API_BASE}/api/therapist-preferences`, preferences, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setSuccess('Inställningar sparade framgångsrikt!');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      setError('Misslyckades att spara inställningar');
      console.error('Fel vid sparning av inställningar:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleApproachToggle = (approachId) => {
    setPreferences(prev => ({
      ...prev,
      therapeutic_approaches: prev.therapeutic_approaches.includes(approachId)
        ? prev.therapeutic_approaches.filter(id => id !== approachId)
        : [...prev.therapeutic_approaches, approachId]
    }));
  };

  const handleTechniqueToggle = (techniqueId) => {
    setPreferences(prev => ({
      ...prev,
      intervention_techniques: prev.intervention_techniques.includes(techniqueId)
        ? prev.intervention_techniques.filter(id => id !== techniqueId)
        : [...prev.intervention_techniques, techniqueId]
    }));
  };

  const getApproachInfo = (approach) => {
    return options.therapeutic_approaches.find(a => a.id === approach) || { label: approach, description: '' };
  };

  const getTechniqueInfo = (technique) => {
    return options.intervention_techniques.find(t => t.id === technique) || { label: technique, category: 'Övrigt' };
  };

  const groupTechniquesByCategory = () => {
    const grouped = {};
    options.intervention_techniques.forEach(technique => {
      const category = getCategoryInSwedish(technique.category);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(technique);
    });
    return grouped;
  };

  const getCategoryInSwedish = (category) => {
    const categoryTranslations = {
      'Cognitive': 'Kognitiva',
      'Emotional': 'Emotionella', 
      'Behavioral': 'Beteende',
      'Insight': 'Insiktsgivande',
      'Other': 'Övrigt'
    };
    return categoryTranslations[category] || category;
  };

  // Translation functions for displaying Swedish text while keeping English values in DB
  const translateApproach = (approach) => {
    const translations = {
      'CBT': 'KBT (Kognitiv beteendeterapi)',
      'Schema Therapy': 'Schematerapi',
      'Psychodynamic': 'Psykodynamisk terapi',
      'Humanistic': 'Humanistisk terapi',
      'Mindfulness': 'Mindfulness',
      'ACT': 'ACT (Acceptance and Commitment Therapy)',
      'DBT': 'DBT (Dialektisk beteendeterapi)',
      'Solution-Focused': 'Lösningsfokuserad terapi',
      'Cognitive Behavioral Therapy (CBT)': 'KBT (Kognitiv beteendeterapi)',
      'Humanistic/Person-Centered': 'Humanistisk/Personcentrerad',
      'Solution-Focused Brief Therapy': 'Lösningsfokuserad korttidsterapi',
      'Mindfulness-Based': 'Mindfulnessbaserad',
      'Family Systems': 'Familjesystemisk',
      'Acceptance and Commitment Therapy': 'ACT (Acceptance and Commitment Therapy)'
    };
    return translations[approach.label] || approach.label;
  };

  const translateApproachDescription = (approach) => {
    const descriptions = {
      'Thought-behavior connections, cognitive restructuring': 'Tanke-beteende kopplingar, kognitiv omstrukturering',
      'Early schemas, childhood patterns, limited reparenting': 'Tidiga scheman, barndomsmönster, begränsad omföräldrande',
      'Unconscious patterns, insight, family dynamics': 'Omedvetna mönster, insikt, familjedynamik',
      'Validation, self-acceptance, client-led': 'Validering, självacceptans, klientlett',
      'What works, scaling questions, brief interventions': 'Vad som fungerar, skalningsfrågor, korta interventioner',
      'Present moment, acceptance, emotional regulation': 'Nuvarande ögonblick, acceptans, emotionell reglering',
      'Family dynamics, systemic influences': 'Familjedynamik, systemiska influenser',
      'Values, psychological flexibility, committed action': 'Värderingar, psykologisk flexibilitet, engagerat handlande'
    };
    return descriptions[approach.description] || approach.description;
  };

  const translateTechnique = (technique) => {
    const translations = {
      'Thought Records': 'Tankeprotokoll',
      'Behavioral Experiments': 'Beteendeexperiment',
      'Exposure Therapy': 'Exponering',
      'Cognitive Restructuring': 'Kognitiv omstrukturering',
      'Mindfulness Exercises': 'Mindfulness-övningar',
      'Grounding Techniques': 'Jordningstekniker',
      'Emotional Validation': 'Emotionell validering',
      'Values Clarification': 'Värdeklarhet',
      'Behavioral Activation': 'Beteendeaktivering',
      'Progressive Muscle Relaxation': 'Progressiv muskelavslappning',
      'Interpretation': 'Tolkning',
      'Free Association': 'Fri association',
      'Dream Analysis': 'Drömanalys',
      'Transference Work': 'Överföringsarbete',
      'Activity Scheduling': 'Aktivitetsschemaläggning',
      'Homework Assignments': 'Hemuppgifter',
      'Empty Chair Technique': 'Tom stol-teknik',
      'Exploring Childhood Patterns': 'Utforska barndomsmönster',
      'Journaling Prompts': 'Dagboksprompts'
    };
    return translations[technique.label] || technique.label;
  };

  const translateCommunicationStyle = (style) => {
    const translations = {
      'Warm and empathetic': 'Varm och empatisk',
      'Professional and structured': 'Professionell och strukturerad',
      'Casual and supportive': 'Avslappnad och stödjande',
      'Formal and analytical': 'Formell och analytisk',
      'Warm & Empathetic': 'Varm och empatisk',
      'Professional & Structured': 'Professionell och strukturerad',
      'Direct & Challenge-Oriented': 'Direkt och utmaningsinriktad',
      'Gentle & Non-Directive': 'Mild och icke-styrande'
    };
    return translations[style.label] || style.label;
  };

  const translateCommunicationDescription = (style) => {
    const translations = {
      'Uses warm, supportive language with deep empathy': 'Använder varma, stödjande formuleringar och visar djup empati',
      'Maintains professional boundaries with clear structure': 'Upprätthåller professionella gränser med tydlig struktur',
      'Relaxed tone focused on support and encouragement': 'Avslappnad ton med fokus på stöd och uppmuntran',
      'Formal approach with analytical and objective tone': 'Formell approach med analytisk och objektiv ton',
      'Highly supportive and nurturing': 'Mycket stödjande och omvårdande',
      'Clear boundaries, organized': 'Tydliga gränser, organiserad',
      'Comfortable with confrontation': 'Bekväm med konfrontation',
      'Client-led, minimal guidance': 'Klientlett, minimal vägledning'
    };
    return translations[style.description] || style.description;
  };

  const translateResponseStyle = (style) => {
    const translations = {
      'Detailed and explanatory': 'Detaljerad och förklarande',
      'Concise and actionable': 'Kortfattad och handlingsinriktad',
      'Reflective and questioning': 'Reflekterande och ifrågasättande',
      'Direct and solution-focused': 'Direkt och lösningsfokuserad',
      'Brief & Focused': 'Kort och fokuserad',
      'Detailed & Explanatory': 'Detaljerad och förklarande',
      'Exploratory & Open-Ended': 'Utforskande och öppen',
      'Supportive & Validating': 'Stödjande och validerande'
    };
    return translations[style.label] || style.label;
  };

  const translateResponseDescription = (style) => {
    const translations = {
      'Provides comprehensive explanations and background information': 'Ger omfattande förklaringar och bakgrundsinformation',
      'Concise responses focused on practical actions': 'Kortfattade svar fokuserade på praktiska åtgärder',
      'Encourages reflection through thoughtful questions': 'Uppmuntrar reflektion genom genomtänkta frågor',
      'Direct approach focused on solutions and outcomes': 'Direkt approach fokuserad på lösningar och resultat',
      'Concise, to-the-point responses': 'Kortfattade, rakt på sak-svar',
      'Thorough explanations and context': 'Grundliga förklaringar och sammanhang',
      'Lots of questions and reflection': 'Många frågor och reflektion',
      'Emphasis on emotional support': 'Fokus på emotionellt stöd'
    };
    return translations[style.description] || style.description;
  };

  const translateCrisisApproach = (approach) => {
    const translations = {
      'Conservative': 'Konservativ',
      'Balanced': 'Balanserad',
      'Active': 'Aktiv',
      'Safety-first': 'Säkerhet först',
      'Safety-First': 'Säkerhet först'
    };
    return translations[approach.label] || approach.label;
  };

  const translateCrisisDescription = (approach) => {
    const translations = {
      'Recommends professional help early for any serious concerns': 'Rekommenderar professionell hjälp tidigt för alla allvarliga problem',
      'Balances support with professional help recommendations when appropriate': 'Balanserar stöd med rekommendationer för professionell hjälp när lämpligt',
      'Provides active support and guidance before suggesting professional help': 'Ger aktivt stöd och vägledning innan professionell hjälp föreslås',
      'Prioritizes safety above all with immediate professional referrals': 'Prioriterar säkerhet över allt annat med omedelbar professionell referral',
      'Always refer to human therapist for concerning content': 'Hänvisar alltid till mänsklig terapeut för oroande innehåll',
      'Provide support while encouraging professional help': 'Ge stöd samtidigt som professionell hjälp uppmuntras',
      'Offer immediate coping strategies before referral': 'Erbjud omedelbara copingstrategier före hänvisning',
      'Immediate emergency resources for crisis language': 'Omedelbara akutresurser för krisspråk'
    };
    return translations[approach.description] || approach.description;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3 text-gray-600">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span>Laddar inställningar...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Rubrik */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Inställningar för AI-terapi</h2>
        <p className="text-gray-600">
          Konfigurera hur din AI-terapeut kommer att interagera med klienter. Dessa inställningar personifierar AI:ns 
          terapeutiska approach, kommunikationsstil och interventionstekniker.
        </p>
      </div>

       {/* Spara-knapp */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Spara dina inställningar</h3>
            <p className="text-gray-600 text-sm">
              Din AI-terapeut kommer att använda dessa inställningar för alla framtida klientinteraktioner.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="cursor-pointer px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sparar...</span>
              </>
            ) : (
              <>
                <Icon name="save" size={16} />
                <span>Spara</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Statusmeddelanden */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="alert-circle" size={20} className="text-red-600 mr-3" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="check-circle" size={20} className="text-green-600 mr-3" />
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* Terapeutiska metoder */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
            <Icon name="brain" size={20} className="text-blue-600 mr-2" />
            Terapeutiska metoder
          </h3>
          <p className="text-gray-600 text-sm">
            Välj de terapeutiska metoder du vill att AI:n ska använda. Du kan välja flera metoder för en integrativ stil.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {options.therapeutic_approaches.map((approach) => (
            <div
              key={approach.id}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                preferences.therapeutic_approaches.includes(approach.id)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleApproachToggle(approach.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={preferences.therapeutic_approaches.includes(approach.id)}
                        onChange={() => handleApproachToggle(approach.id)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                        preferences.therapeutic_approaches.includes(approach.id)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        {preferences.therapeutic_approaches.includes(approach.id) && (
                          <Icon name="check" size={12} className="text-white" />
                        )}
                      </div>
                    </div>
                    <h4 className="font-medium text-gray-900 ml-3">{translateApproach(approach)}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{translateApproachDescription(approach)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {preferences.therapeutic_approaches.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Valda metoder:</h4>
            <div className="flex flex-wrap gap-2">
              {preferences.therapeutic_approaches.map(id => {
                const approach = getApproachInfo(id);
                return (
                  <span key={id} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {translateApproach(approach)}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Interventionstekniker */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
            <Icon name="wrench" size={20} className="text-green-600 mr-2" />
            Interventionstekniker
          </h3>
          <p className="text-gray-600 text-sm">
            Välj specifika tekniker som AI:n ska använda under terapisessioner.
          </p>
        </div>

        {Object.entries(groupTechniquesByCategory()).map(([category, techniques]) => (
          <div key={category} className="mb-6">
            <h4 className="font-medium text-gray-800 mb-3 pb-2 border-b border-gray-200">
              {category} tekniker
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {techniques.map((technique) => (
                <label
                  key={technique.id}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                    preferences.intervention_techniques.includes(technique.id)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={preferences.intervention_techniques.includes(technique.id)}
                      onChange={() => handleTechniqueToggle(technique.id)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                      preferences.intervention_techniques.includes(technique.id)
                        ? 'bg-green-600 border-green-600'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      {preferences.intervention_techniques.includes(technique.id) && (
                        <Icon name="check" size={10} className="text-white" />
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 ml-3">{translateTechnique(technique)}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {preferences.intervention_techniques.length > 0 && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-medium text-green-900 mb-2">
              Valda tekniker ({preferences.intervention_techniques.length}):
            </h4>
            <div className="flex flex-wrap gap-2">
              {preferences.intervention_techniques.map(id => {
                const technique = getTechniqueInfo(id);
                return (
                  <span key={id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {translateTechnique(technique)}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Kommunikations- och svarsstilar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Kommunikationsstil */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="message-circle" size={20} className="text-purple-600 mr-2" />
            Kommunikationsstil
          </h3>
          <div className="space-y-3">
            {options.communication_styles.map((style) => (
              <label
                key={style.value}
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                  preferences.communication_style === style.value
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="relative mt-1">
                  <input
                    type="radio"
                    name="communication_style"
                    value={style.value}
                    checked={preferences.communication_style === style.value}
                    onChange={(e) => setPreferences(prev => ({ ...prev, communication_style: e.target.value }))}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
                    preferences.communication_style === style.value
                      ? 'bg-purple-600 border-purple-600'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}>
                    {preferences.communication_style === style.value && (
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    )}
                  </div>
                </div>
                <div className="ml-3">
                  <div className="font-medium text-gray-900">{translateCommunicationStyle(style)}</div>
                  <div className="text-sm text-gray-600">{translateCommunicationDescription(style)}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Svarsstil */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="file-text" size={20} className="text-orange-600 mr-2" />
            Svarsstil
          </h3>
          <div className="space-y-3">
            {options.response_styles.map((style) => (
              <label
                key={style.value}
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                  preferences.response_style === style.value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="relative mt-1">
                  <input
                    type="radio"
                    name="response_style"
                    value={style.value}
                    checked={preferences.response_style === style.value}
                    onChange={(e) => setPreferences(prev => ({ ...prev, response_style: e.target.value }))}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
                    preferences.response_style === style.value
                      ? 'bg-orange-600 border-orange-600'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}>
                    {preferences.response_style === style.value && (
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    )}
                  </div>
                </div>
                <div className="ml-3">
                  <div className="font-medium text-gray-900">{translateResponseStyle(style)}</div>
                  <div className="text-sm text-gray-600">{translateResponseDescription(style)}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Krishantering */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Icon name="shield" size={20} className="text-red-600 mr-2" />
          Krishanteringsmetod
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {options.crisis_approaches.map((approach) => (
            <label
              key={approach.value}
              className={`flex items-start p-4 border rounded-lg cursor-pointer transition-all ${
                preferences.crisis_approach === approach.value
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="relative mt-1">
                <input
                  type="radio"
                  name="crisis_approach"
                  value={approach.value}
                  checked={preferences.crisis_approach === approach.value}
                  onChange={(e) => setPreferences(prev => ({ ...prev, crisis_approach: e.target.value }))}
                  className="sr-only"
                />
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
                  preferences.crisis_approach === approach.value
                    ? 'bg-red-600 border-red-600'
                    : 'border-gray-300 hover:border-gray-400'
                }`}>
                  {preferences.crisis_approach === approach.value && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
              </div>
              <div className="ml-3">
                <div className="font-medium text-gray-900">{translateCrisisApproach(approach)}</div>
                <div className="text-sm text-gray-600">{translateCrisisDescription(approach)}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Spara-knapp */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Spara dina inställningar</h3>
            <p className="text-gray-600 text-sm">
              Din AI-terapeut kommer att använda dessa inställningar för alla framtida klientinteraktioner.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 cursor-pointer"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sparar...</span>
              </>
            ) : (
              <>
                <Icon name="save" size={16} />
                <span>Spara</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TherapistPreferences;
